import { Skeleton, SkeletonRegion } from "../ui/skeleton.js";

// Loading placeholders for the Leave & WFH tabs. Each mirrors the footprint of the
// content that replaces it (same card heights and grid), so nothing jumps on load.

export type LeaveSkeletonTab = "my" | "calendar" | "team";

function RequestCardSkeleton() {
  return (
    <div className="flex h-[96px] flex-col justify-between rounded-lg border border-surface-200 bg-surface-0 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-16" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-[72px]" />
          <Skeleton className="h-8 w-[64px]" />
        </div>
      </div>
      <Skeleton className="h-3 w-48" />
      <Skeleton className="h-3 w-40" />
    </div>
  );
}

function TeamSkeleton() {
  return (
    <>
      <Skeleton className="mb-3 h-3 w-36" />
      <div className="grid grid-cols-1 gap-2.5 min-[1100px]:grid-cols-2">
        {Array.from({ length: 6 }, (_, i) => <RequestCardSkeleton key={i} />)}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-8 w-44" />
      </div>
    </>
  );
}

function MySkeleton() {
  return (
    <>
      <div className="mb-6 grid grid-cols-2 gap-3 min-[900px]:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="rounded-lg border border-surface-200 bg-surface-0 p-3">
            <Skeleton className="mb-3 h-3 w-24" />
            <Skeleton className="mb-3 h-7 w-16" />
            <Skeleton className="h-1.5 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="mb-3 h-3 w-32" />
      <div className="flex flex-col gap-2.5">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border border-surface-200 bg-surface-0 p-3">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-52" />
              <Skeleton className="h-3 w-36" />
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </>
  );
}

function CalendarSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-surface-200 bg-surface-0">
      <div className="flex items-center justify-between border-b border-surface-200 px-4 py-3">
        <Skeleton className="h-4 w-32" />
        <div className="flex gap-1.5">
          <Skeleton className="h-8 w-9" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-9" />
        </div>
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: 35 }, (_, i) => (
          <div key={i} className="min-h-[84px] border-b border-r border-surface-100 p-1.5">
            <Skeleton className="h-3 w-4" />
            {i % 3 !== 2 && (
              <div className="mt-2 flex">
                <Skeleton className="size-6 rounded-full" />
                <Skeleton className="-ml-2 size-6 rounded-full" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function LeavePageSkeleton({ tab }: { tab: LeaveSkeletonTab }) {
  return (
    <SkeletonRegion label="Loading leave" className="px-4 py-3">
      {tab === "team" ? <TeamSkeleton /> : tab === "calendar" ? <CalendarSkeleton /> : <MySkeleton />}
    </SkeletonRegion>
  );
}
